import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";

import { ThemeProvider } from "flowbite-react";
import { Provider } from "react-redux";
import { Route, Routes } from "react-router";
import { BrowserRouter } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { PersistGate } from "redux-persist/integration/react";
import { enableSentryLogs, isDev, isLocalhost } from "./config/config";
import theme from "./flowbite-theme";
import "./index.css";
import DashboardPage from "./pages";
import ExerciseListPage from "./pages/exercises/list";
import LoginPage from "./pages/login";
import ProgramsListPage from "./pages/programs/list";
import Spotlight from "./pages/spotlight/spotlight";
import TrainersPage from "./pages/trainers/list";
import UserListPage from "./pages/users/list";
import UserProfilePage from "./pages/users/profile";
import MediaListPage from "./pages/media/list";
import WorkoutsListPage from "./pages/workouts/list";
import TraditionalWorkoutsListPage from "./pages/traditional-workouts/list";
import TraditionalWorkoutPage from "./pages/traditional-workouts/TraditionalWorkoutPage";
import NotificationsPage from "./pages/notifications/NotificationsPage";
import RadioListPage from "./pages/radio/list";
import ManagePlaylistPage from "./pages/radio/manage";
import MembershipSettingsPage from "./pages/membership/settings";
import ABTestingPage from "./pages/membership/ab-testing";
import ManualOnboardingPage from "./pages/membership/manual-onboarding";
import InvitesPage from "./pages/membership/invites";
import { persistor, store } from "./redux//store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const container = document.getElementById("root");

const queryClient = new QueryClient();

if (!container) {
  throw new Error("React root element doesn't exist!");
}

/* Sentry setup */
Sentry.init({
  dsn: "https://0dd070978e421801bc30fee75810bf20@o4506696022163456.ingest.us.sentry.io/4507900720906240",
  tunnel: "/api/analytics/sentry-tunnel",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.replayCanvasIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["error"] }),
  ],
  enableLogs: true,
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/(?:[\w-]+\.)*sweatsonic\.com$/,
  ],
  replaysSessionSampleRate: isLocalhost || isDev ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  enabled: enableSentryLogs || !isLocalhost,
  environment: isLocalhost ? "dev" : isDev ? "dev" : "prod",
});

// Update global test function for sentry logs to use a generic message
(window as any).testSentryLog = () => {
  Sentry.captureMessage("Generic test Sentry log event");
};

/* React setup */
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} index />
              <Route path="/spotlight" element={<Spotlight />} />
              <Route path="/" element={<DashboardPage />} />
              <Route path="/workouts" element={<WorkoutsListPage />} />
              <Route path="/music-workouts" element={<WorkoutsListPage />} />
              <Route
                path="/traditional-workouts"
                element={<TraditionalWorkoutsListPage />}
              />
              <Route
                path="/traditional-workouts/new"
                element={<TraditionalWorkoutPage />}
              />
              <Route
                path="/traditional-workouts/:id/edit"
                element={<TraditionalWorkoutPage />}
              />
              <Route path="/trainers" element={<TrainersPage />} />
              <Route path="/exercises" element={<ExerciseListPage />} />
              <Route path="/media" element={<MediaListPage />} />
              <Route path="/programs" element={<ProgramsListPage />} />
              <Route path="/users/list" element={<UserListPage />} />
              <Route path="/users/profile" element={<UserProfilePage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/radio" element={<RadioListPage />} />
              <Route
                path="/radio/manage/:id"
                element={<ManagePlaylistPage />}
              />
              <Route
                path="/membership/settings"
                element={<MembershipSettingsPage />}
              />
              <Route
                path="/membership/ab-testing"
                element={<ABTestingPage />}
              />
              <Route
                path="/membership/manual-onboarding"
                element={<ManualOnboardingPage />}
              />
              <Route path="/membership/invites" element={<InvitesPage />} />
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </PersistGate>
  </Provider>,
);
